// Enums
import { PostMessageOutgoingAction } from '@/enums/PostMessageAction';

// Libraries
import debounce from 'lodash/debounce';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { History } from 'history';

// PostMessages
import { AbstractStrategy } from './abstract-strategy';

// Redux
import store from '@/redux/store';
import { transformFDC3Portfolio } from '@/redux/modules/portfolios';
import { GET_CONTACT_PORTFOLIOS, SET_CONTACT_PORTFOLIOS } from '@/redux/modules/contacts';
import { getHouseholdPortfolios } from '@/redux/modules/household';

// Types
import { PortfolioFDC3 } from '@/types/portfolio-fdc3';
import { PostMessageRetry } from '@/redux/modules/types/post-message';
import { ReduxState } from '@/redux/modules/types';
import Portfolio from '@/types/portfolio';

// Utils
import { PostMessageSender } from '@/utils/post-messages/outgoing-messages/post-message-sender';
import { addHoldingsToPortfolios } from '@/utils/portfolios';

export class GetPortfoliosStrategy extends AbstractStrategy {
  async execute(dispatch: ThunkDispatch<ReduxState, null, AnyAction>, context: PortfolioFDC3[], history: History<unknown>): Promise<void> {
    // Trigger a retry if we receive an empty array of Portfolios AND if the retry limit hasn't been reached
    const retryStatus: PostMessageRetry = store.getState().postMessage.retry.getPortfolios;
    const shouldRetry: boolean = this.evaluateRetry(PostMessageOutgoingAction.FACTSET_GET_PORTFOLIOS, retryStatus);
    const triggerRetry: () => void = debounce(
      () => PostMessageSender.getPortfolios(dispatch, store.getState, retryStatus.user, retryStatus.context.portfolios!),
      retryStatus.delay,
    );

    if (!context.length && shouldRetry) {
      triggerRetry();
      return;
    }

    // Transform the FDC3 Portfolios into CapIntel Portfolios by calling the API
    const arrayOfTransform: Promise<Portfolio>[] = [];
    context.forEach((portfolio: PortfolioFDC3) => {
      const customProxyMapping = store.getState().auth.user?.appConfig?.customProxyMapping;
      arrayOfTransform.push(transformFDC3Portfolio(portfolio, true, customProxyMapping));
    });

    const capIntelPortfolios: Portfolio[] = await Promise.all(arrayOfTransform);

    // Update the Redux store with the new portfolios holdings
    const { contacts } = store.getState();
    const portfoliosWithHoldings = addHoldingsToPortfolios(contacts.postMessagePortfolios, capIntelPortfolios);
    dispatch({ type: SET_CONTACT_PORTFOLIOS.type, portfolios: portfoliosWithHoldings });
    dispatch({ type: `${GET_CONTACT_PORTFOLIOS.type}_SUCCESS`, data: { data: { portfolios: capIntelPortfolios, next: undefined } }, params: {} });
    dispatch(getHouseholdPortfolios());
  }
}
